import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Box, Switch, FormControlLabel, IconButton, List, Stack, TextField, Tooltip, Button, Typography } from '@mui/material';
import { UserData } from '../../interfaces';
import { Delete, Search, Clear } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import DeleteUsers from './DeleteUsers';
import {
  GridColDef,
  GridFilterModel,
  GridRowParams,
  GridValidRowModel,
  GridLogicOperator,
  DataGridPro,
  useGridApiRef,
  GridEditDateCell,
  GridColTypeDef,
  GRID_DATE_COL_DEF,
  getGridStringOperators,
  Toolbar,
  QuickFilter,
  QuickFilterControl,
  QuickFilterClear,
} from '@mui/x-data-grid-pro';
import { nbNO } from '@mui/x-data-grid/locales';
import { formatDateOnly } from '../../Formatters';
import { GET_USERS } from '../user.graphql';
import { useQuery } from '@apollo/client/react';

const CustomToolbar = () => {
  const [container, setContainer] = useState<HTMLElement | null>(null);
  useEffect(() => {
    setContainer(document.getElementById('filter-panel'));
  }, []);
  if (!container) return null;
  return createPortal(
    <Toolbar style={{ width: '100%', padding: 0 }}>
      <QuickFilter expanded style={{ width: '100%' }}>
        <QuickFilterControl
          render={({ ref, ...controlProps }, state) => (
            <TextField
              {...controlProps}
              inputRef={ref}
              size="small"
              variant='standard'
              placeholder="Søk på navn, e-post eller brukernavn..."
              fullWidth
              slotProps={{
                input: {
                  startAdornment: <Search fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />,
                  endAdornment: state.value ? (
                    <QuickFilterClear
                      render={({ ref: clearRef, ...clearProps }) => (
                        <IconButton {...clearProps} ref={clearRef} size="small">
                          <Clear fontSize="small" />
                        </IconButton>
                      )}
                    />
                  ) : null,
                },
              }}
            />
          )}
        />
      </QuickFilter>
    </Toolbar>,
    container,
  );
};

function loadBoolean(key, defaultValue): boolean {
  const item = sessionStorage.getItem(key);
  const value = item != null ? item === 'true' : defaultValue;
  return value;
}

function loadNumber(key, defaultValue): number {
  const item = sessionStorage.getItem(key);
  const value = item != null ? parseInt(item) : defaultValue;
  return value;
}

export function UserSearchableTable() {
  const apiRef = useGridApiRef();

  const [selected, setSelected] = React.useState<string[]>([]);

  const [openConfirm, setOpenConfirm] = useState(false);

  const [onlyActive, setOnlyActive] = useState(() => loadBoolean('onlyActive', false));
  const [activeSingleDocument, setActiveSingleDocument] = useState(() => loadBoolean('activeSingleDocument', false));

  const [nonActiveSingleDocument, setNonActiveSingleDocument] = useState(() => loadBoolean('nonActiveSingleDocument', false));
  const [nonActiveForNumberYears, setNonActiveForNumberYears] = useState(() => loadNumber('nonActiveForNumberYears', 5));

  const [nonActiveNumberYears, setNonActiveNumberYears] = React.useState<string>(nonActiveForNumberYears.toString());

  useEffect(() => {
    sessionStorage.setItem('onlyActive', onlyActive.toString());
    sessionStorage.setItem('activeSingleDocument', activeSingleDocument.toString());
    sessionStorage.setItem('nonActiveSingleDocument', nonActiveSingleDocument.toString());
    sessionStorage.setItem('nonActiveForNumberYears', nonActiveForNumberYears.toString());
  }, [onlyActive, activeSingleDocument, nonActiveSingleDocument, nonActiveForNumberYears]);

  const [paginationModel, setPaginationModel] = React.useState({
    pageSize: 25,
    page: 0,
  });

  const [filter, setFilter] = useState('');

  const pageSize = paginationModel.pageSize;
  const page = paginationModel.page;

  const hasFilter =
    onlyActive ||
    activeSingleDocument ||
    nonActiveSingleDocument ||
    (filter !== '' && filter !== '{}');

  const { loading, data, error, refetch } = useQuery<{ users: UserData }>(GET_USERS, {
    variables: {
      page,
      pageSize,
      onlyActive,
      activeSingleDocument,
      nonActiveSingleDocument,
      nonActiveForNumberYears,
      filter,
    },
    fetchPolicy: 'no-cache',
    skip: !hasFilter,
  });

  const isLoading = hasFilter && (loading || !data);

  const rowCount = hasFilter ? data?.users.count ?? 0 : 0;

  const users = hasFilter ? data?.users.users ?? [] : [];

  const [rowCountState, setRowCountState] = React.useState(rowCount);
  React.useEffect(() => {
    setRowCountState((prevRowCountState) => (rowCount !== undefined ? rowCount : prevRowCountState));
  }, [rowCount, setRowCountState]);

  const dateColumnType: GridColTypeDef<Date, string> = {
    ...GRID_DATE_COL_DEF,
    resizable: false,
    renderEditCell: (params) => {
      return <GridEditDateCell {...params} />;
    },
    valueFormatter: (value: Date) => {
      if (value) {
        return formatDateOnly(value);
      }
      return '';
    },
  };

  const filterOperators = getGridStringOperators().filter(({ value }) =>
    ['contains', 'equals', 'startsWith', 'endsWith', 'isEmpty', 'isNotEmpty'].includes(value),
  );

  const columns: GridColDef[] = [
    {
      field: 'displayName',
      headerName: 'Navn',
      minWidth: 160,
      flex: 1,
      align: 'left',
      headerAlign: 'left',
      filterOperators: filterOperators,
      renderCell: (params) => {
        return <Link to={`/user/${params.id}`}>{params.value}</Link>;
      },
    },
    {
      field: 'email',
      headerName: 'E-post',
      minWidth: 160,
      flex: 1,
      align: 'left',
      headerAlign: 'left',
      filterOperators: filterOperators,
    },
    {
      field: 'lastLoggedIn',
      ...dateColumnType,
      headerName: 'Sist innlogget',
      minWidth: 160,
      flex: 1,
      align: 'left',
      headerAlign: 'left',
    },
    {
      field: 'loginId',
      headerName: 'Brukernavn',
      minWidth: 160,
      flex: 1,
      align: 'left',
      headerAlign: 'left',
      filterOperators: filterOperators,
    },
  ];

  const rows: GridValidRowModel[] = users.map((x) => {
    return {
      id: x.id,
      isDeletable: !x.isAdminuserOnActiveLicense,
      displayName: x.displayName,
      email: x.email,
      lastLoggedIn: x.lastLoggedIn,
      loginId: x.loginId,
    };
  });

  const onFilterChange = React.useCallback(
    (filterModel: GridFilterModel) => {
      var queryOptionsJson = JSON.stringify({ ...filterModel });
      if (queryOptionsJson) setFilter(queryOptionsJson);
    },
    [setFilter],
  );

  const removeFromList = React.useCallback(() => refetch(), [refetch]);

  return (
    <>
      <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between" }}>
        <Stack direction="row" sx={{ alignItems: 'center' }}>
          <FormControlLabel
            title="Brukere med lisens på en serie."
            control={
              <Switch
                onChange={() => {
                  setNonActiveSingleDocument(false);
                  return setOnlyActive(!onlyActive);
                }}
                checked={onlyActive}
              />
            }
            label="Aktive"
          />

          <FormControlLabel
            title="Brukere med lisens en eller flere enkeltanvisninger."
            control={
              <Switch
                onChange={() => {
                  setNonActiveSingleDocument(false);
                  return setActiveSingleDocument(!activeSingleDocument);
                }}
                checked={activeSingleDocument}
              />
            }
            label="Aktive Single"
          />

          <FormControlLabel
            title="Brukere ikke tilknyttet lisens."
            control={
              <Switch
                onChange={() => {
                  setOnlyActive(false);
                  setActiveSingleDocument(false);
                  return setNonActiveSingleDocument(!nonActiveSingleDocument);
                }}
                checked={nonActiveSingleDocument}
              />
            }
            label="Ikke aktive."
          />

          <Button
            title="Slett merkede brukere. Brukere som administrerer lisenser kan ikke slettes."
            disabled={selected.length === 0}
            sx={{
              marginRight: 15,
              marginLeft: 15,
              margin: 1,
            }}
            variant="contained"
            color="secondary"
            onClick={() => setOpenConfirm(true)}
            startIcon={<Delete />}>
            Slett
          </Button>

          {openConfirm && (
            <DeleteUsers
              open={openConfirm}
              onClose={() => setOpenConfirm(false)}
              userId={selected.map((c) => Number(c))}
              setSelected={setSelected}
              users={users}
              removeFromList={removeFromList}
            />
          )}

          {nonActiveSingleDocument && (
            <>
              <Tooltip title="År siden siste innlogging.">
                <TextField
                  type="text"
                  style={{ width: 75 }}
                  label="År"
                  slotProps={{ htmlInput: { min: 0, max: 25, step: 1 } }}
                  value={nonActiveNumberYears}
                  onChange={(e) => {
                    if (e.target.value !== '') {
                      var years = parseInt(e.target.value);
                      if (!isNaN(years)) {
                        setNonActiveNumberYears(e.target.value);
                      }
                    } else {
                      setNonActiveNumberYears('');
                    }
                  }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      setNumberOfYearsOnParent();
                    }
                  }}
                  onBlur={(e) => {
                    setNumberOfYearsOnParent();
                  }}
                />
              </Tooltip>
            </>
          )}
        </Stack>
      </Stack>
      {error && <Typography>{error.message}</Typography>}
      <Box id="filter-panel" sx={{ mt: 1 }} />
      <div>
        <DataGridPro
          apiRef={apiRef}
          autoHeight
          density="compact"
          rows={rows}
          columns={columns}
          checkboxSelection
          filterDebounceMs={500}
          showToolbar
          isRowSelectable={function (params: GridRowParams) {
            return params.row.isDeletable;
          }}
          onRowSelectionModelChange={(newRowSelectionModel) => {
            setSelected(Array.from(newRowSelectionModel.ids).map((c) => c.toString()));
          }}
          localeText={nbNO.components.MuiDataGrid.defaultProps.localeText}
          pagination
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: paginationModel.pageSize,
                page: paginationModel.page,
              },
            },
            filter: {
              filterModel: {
                items: [],
                quickFilterLogicOperator: GridLogicOperator.Or,
              },
            },
          }}
          loading={isLoading}
          rowCount={rowCountState}
          paginationMode="server"
          pageSizeOptions={[10, 25, 100]}
          filterMode="server"
          onFilterModelChange={onFilterChange}
          onPaginationModelChange={setPaginationModel}
          slots={{
            toolbar: CustomToolbar,
            noRowsOverlay: () => (
              <Stack sx={{ p: 2, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  {hasFilter
                    ? 'Ingen brukere matchet søket.'
                    : 'Søk eller velg et filter for å vise brukere.'}
                </Typography>
              </Stack>
            ),
          }}
          sx={{
            width: '100%',
          }}
        />
      </div>
    </>
  );

  function setNumberOfYearsOnParent() {
    var years = parseInt(nonActiveNumberYears);
    if (!isNaN(years)) {
      setNonActiveForNumberYears(parseInt(nonActiveNumberYears));
    } else {
      setNonActiveForNumberYears(5);
    }
  }
}
